import { Box, Stack, Typography } from '@mui/material';
import { LexicalEditor } from 'lexical';
import { useCallback, useState } from 'react';

import {
  ButtonWithPopover,
  ButtonWithPopoverSize,
} from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { ButtonVariant } from '@/components/form/baseInputs/Button/types';
import { TableIcon } from '@/components/icons/TableIcon';
import { COLORS } from '@/styles/tokens/colors';

import { insertTable } from './tableUtils';
import { iconProps } from './ToolbarPlugin.utils';

interface TableInsertButtonProps {
  editor: LexicalEditor;
  isInTable?: boolean;
  iconSize?: number;
  size?: ButtonWithPopoverSize;
  variant?: ButtonVariant;
  square?: boolean;
  buttonSx?: React.ComponentProps<typeof ButtonWithPopover>['buttonSx'];
}

const GRID_SIZE = 5;

export function TableInsertButton({
  editor,
  isInTable = false,
  iconSize = iconProps.size,
  size = 'sm',
  variant = 'secondary',
  square,
  buttonSx,
}: TableInsertButtonProps) {
  const [hoverCell, setHoverCell] = useState<{
    row: number;
    col: number;
  } | null>(null);

  const handleInsertTable = useCallback(
    (rows: number, cols: number) => {
      insertTable(editor, rows, cols, true);
    },
    [editor]
  );

  if (isInTable) {
    return null;
  }

  const renderGridPicker = () => {
    const grid = [];

    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        const isHighlighted =
          hoverCell !== null && row <= hoverCell.row && col <= hoverCell.col;

        grid.push(
          <Box
            key={`${row}-${col}`}
            onMouseEnter={() => setHoverCell({ row, col })}
            onClick={() => handleInsertTable(row + 1, col + 1)}
            sx={{
              width: 20,
              height: 20,
              border: `1px solid ${COLORS.GRAY[300]}`,
              backgroundColor: isHighlighted
                ? COLORS.BLUE[100]
                : COLORS.PRIMITIVES.WHITE,
              cursor: 'pointer',
              transition: 'background-color 0.1s',
              '&:hover': {
                backgroundColor: COLORS.BLUE[100],
              },
            }}
          />
        );
      }
    }

    return (
      <Box
        display="grid"
        gridTemplateColumns={`repeat(${GRID_SIZE}, 20px)`}
        gap={0.5}
        onMouseLeave={() => setHoverCell(null)}
      >
        {grid}
      </Box>
    );
  };

  const getDimensionsText = () => {
    if (!hoverCell) return 'Select table size';
    return `${hoverCell.row + 1} × ${hoverCell.col + 1}`;
  };

  return (
    <ButtonWithPopover
      tooltip="Add table"
      tooltipProps={{
        placement: 'bottom',
      }}
      size={size}
      variant={variant}
      square={square}
      popperVariant="menuBelow"
      popperProps={{
        sx: { zIndex: 999 },
        disablePortal: false,
      }}
      sx={buttonSx}
      label={<TableIcon size={iconSize} />}
    >
      <Box p={2}>
        <Stack spacing={2}>
          {renderGridPicker()}
          <Typography variant="body-xs" textAlign="center">
            {getDimensionsText()}
          </Typography>
        </Stack>
      </Box>
    </ButtonWithPopover>
  );
}
